/**
 * Parti riutilizzabili del dialogo "Dati e relazioni" (Relationships360), condivise tra i modali
 * specifici per tipo (Company360 / Good360 / Trend360):
 *
 *  - UI presentazionale: Chip, RelationSearchSelect, RelationEditor, SectionCard.
 *  - `Relations360Dialog`: la shell + il motore di staging delle relazioni. È GENERICO: gestisce
 *    solo le sezioni relazione (chip + suggerimenti + crea collegamento + salvataggio bulk). Le
 *    eventuali sezioni "edit" (modifica dati dell'entità collegata) sono iniettate dal chiamante
 *    come `EditSectionSpec[]`, così ogni tipo tiene la propria logica nel proprio file.
 *
 * SALVATAGGIO UNICO: nessuna sotto-sezione ha un proprio bottone Salva. Tutto (relazioni + dati
 * delle sezioni edit) si salva con l'unico bottone nel footer del dialogo; ogni sezione con
 * modifiche pendenti mostra il badge "non salvato" sulla propria card. Le modifiche alle relazioni
 * sono accumulate in stato e applicate in UN'UNICA richiesta bulk (delete + create); i collegamenti
 * sono bidirezionali.
 *
 * Per non perdere le modifiche non salvate tornando all'elenco, le sotto-viste edit già visitate
 * restano MONTATE (nascoste con `display: none`) finché il dialogo è aperto.
 */

import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Card,
	CardBody,
} from 'reactstrap';
import Select from 'react-select';
import * as R from 'ramda';
import { registerClass } from '@food/css-manager';
import { t } from '../utils/labels';
import { ENTITY } from '../utils/entities';
import { entity2String } from '../utils/toString';
import { client } from '../utils/client';
import {
	modalHandler,
	parseGraphqlError,
	sentryHandler,
} from '../utils/errors';
import { Spinner } from './MainContentSpinner';
import {
	RelationSection,
	getRelationSections,
	buildRelationsQuery,
	buildSuggestionsQuery,
	makeSearchQuery,
	RELATIONS_LINKS_QUERY,
	RelationLinks,
	emptyLinks,
	parseLinks,
	findJoinIds,
	findKeyedJoinIds,
	applyRelationships360Bulk,
	bidirectionalCreatePairs,
	keyedCreatePair,
	RefPair,
} from '../utils/entities/relationships360';
import { Relation360CreateView } from './CompanyData360EditView';
import {
	EditSaveHandle,
	EditSaveScope,
	RegisterEditSave,
	RegisterSubForm,
	SubFormHandle,
	SubFormScope,
} from './relations360SaveRegistry';

/** Descrittore di una sezione "edit" iniettata dal modale specifico per tipo. */
export interface EditSectionSpec {
	key: string;
	labelKey: string;
	summaryText: string; // testo mostrato nella card
	// `onSaved` NON chiude la sezione: serve solo a segnalare che i dati sul server sono cambiati
	// (creazioni/eliminazioni immediate) e che i riepiloghi vanno ricaricati.
	render: (onSaved: () => void) => React.ReactNode; // sotto-vista di modifica
}

const prepareSuggestions = (section: RelationSection, list: any[]): any[] =>
	section.sortSuggestions
		? R.sortBy(
				(s: any) => (entity2String(section.targetEntity, s) || '').toLowerCase(),
				list,
		  )
		: list;

/** Chiave di sezione usata per la sotto-vista "crea collegamento" di una sezione relazione. */
const createSectionKey = (field: string) => 'create:' + field;

type EntityMap = { [id: string]: any };

type UnifiedActive =
	| { kind: 'relation'; relation: RelationSection }
	| { kind: 'edit'; key: string };

const sectionCardClass = registerClass(
	() => `
	cursor: pointer;
	border: 1px solid #e0e0e0;
	transition: box-shadow .15s ease, border-color .15s ease, background .15s ease;

	& .relations360-edit-hint { color: #2962ff; white-space: nowrap; }

	&:hover {
		border-color: #2962ff;
		background: #f5f8ff;
		box-shadow: 0 2px 8px rgba(41, 98, 255, .15);
	}
	&:hover .relations360-edit-hint { text-decoration: underline; }
`,
);

const chipStyle: React.CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	margin: 4,
	padding: '4px 10px',
	border: '1px solid #d0d0d0',
	borderRadius: 16,
	background: '#f7f7f7',
};

const pendingChipStyle: React.CSSProperties = {
	...chipStyle,
	background: '#eaf7ee',
	borderColor: '#a5d6b0',
};

export const Chip: React.FC<{
	label: string;
	icon: 'plus' | 'minus';
	color: string;
	busy: boolean;
	pending?: boolean;
	onClick: () => void;
}> = ({ label, icon, color, busy, pending, onClick }) => (
	<span style={pending ? pendingChipStyle : chipStyle}>
		{label}
		<button
			type="button"
			className="btn btn-sm btn-link"
			style={{ padding: '0 0 0 6px', color }}
			disabled={busy}
			onClick={onClick}
		>
			<i className={'fa fa-' + icon} />
		</button>
	</span>
);

const SUGGESTIONS_PAGE_SIZE = 10;

/**
 * Chip dei suggerimenti, mostrati a blocchi di SUGGESTIONS_PAGE_SIZE. Il limite è sul numero di
 * chip visibili, non su una pagina fissa: aggiungendo un suggerimento il successivo scorre dentro,
 * e quelli rimossi dai collegati rientrano nella loro posizione della lista.
 */
const SuggestionChips: React.FC<{
	section: RelationSection;
	suggestions: any[];
	busy: boolean;
	onAdd: (entity: any) => void;
}> = ({ section, suggestions, busy, onAdd }) => {
	const [limit, setLimit] = useState(SUGGESTIONS_PAGE_SIZE);
	const visible = suggestions.slice(0, limit);
	const hiddenCount = suggestions.length - visible.length;

	return (
		<>
			{visible.map((s) => (
				<Chip
					key={s.id}
					label={entity2String(section.targetEntity, s)}
					icon="plus"
					color="#28a745"
					busy={busy}
					onClick={() => onAdd(s)}
				/>
			))}
			{hiddenCount > 0 && (
				<div>
					<Button
						size="sm"
						color="link"
						onClick={() => setLimit(visible.length + SUGGESTIONS_PAGE_SIZE)}
					>
						{t`Relations360/showMoreSuggested`}{' '}
						{Math.min(hiddenCount, SUGGESTIONS_PAGE_SIZE)}
					</Button>
					{hiddenCount > SUGGESTIONS_PAGE_SIZE && (
						<Button size="sm" color="link" onClick={() => setLimit(Infinity)}>
							{t`Relations360/showAllSuggested`} ({suggestions.length})
						</Button>
					)}
				</div>
			)}
		</>
	);
};

/** Card cliccabile di una sezione (relazione o edit), con hint "clicca per modificare". */
const SectionCard: React.FC<{
	title: React.ReactNode;
	onClick: () => void;
	children?: React.ReactNode;
}> = ({ title, onClick, children }) => (
	<Card
		className={'margin-bottom ' + sectionCardClass}
		onClick={onClick}
		title={t`Relations360/clickToEdit`}
	>
		<CardBody>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<h5 style={{ margin: 0 }}>{title}</h5>
				<span className="relations360-edit-hint">
					<i className="fa fa-pencil" /> {t`Relations360/clickToEdit`}{' '}
					<i className="fa fa-chevron-right" />
				</span>
			</div>
			{children && <div className="margin-top">{children}</div>}
		</CardBody>
	</Card>
);

const UnsavedBadge: React.FC = () => (
	<span className="text-warning" style={{ marginLeft: 8, fontSize: '0.8em' }}>
		• {t`Relations360/unsaved`}
	</span>
);

const EditSectionHost = React.memo<{
	spec: EditSectionSpec;
	onSaved: () => void;
	register: RegisterEditSave;
}>(({ spec, onSaved, register }) => (
	<EditSaveScope sectionKey={spec.key} register={register}>
		{spec.render(onSaved)}
	</EditSaveScope>
));

/** Come `EditSectionHost`, per la sotto-vista "crea collegamento" di una sezione relazione. */
const RelationCreateHost = React.memo<{
	createKind: 'fairParticipation' | 'companyCertification' | 'goodClaim';
	connectedCompany: any;
	connectedId: string;
	sectionKey: string;
	onSaved: () => void;
	onCancel: () => void;
	register: RegisterEditSave;
}>(
	({
		createKind,
		connectedCompany,
		connectedId,
		sectionKey,
		onSaved,
		onCancel,
		register,
	}) => (
		<EditSaveScope sectionKey={sectionKey} register={register}>
			<Relation360CreateView
				createKind={createKind}
				connectedCompany={connectedCompany}
				connectedId={connectedId}
				onSaved={onSaved}
				onCancel={onCancel}
			/>
		</EditSaveScope>
	),
);

/** Select di ricerca per aggiungere un'entità del tipo della sezione. */
const RelationSearchSelect: React.FC<{
	section: RelationSection;
	disabledIds: string[];
	busy: boolean;
	onPick: (entity: any) => void;
}> = ({ section, disabledIds, busy, onPick }) => {
	const search = useMemo(() => makeSearchQuery(section), [section.field]);
	const [text, setText] = useState('');
	const [options, setOptions] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		let active = true;
		const timeout = setTimeout(() => {
			setLoading(true);
			search(text)
				.then((res) => {
					if (!active) return;
					setOptions(
						res.entities
							.filter((e: any) => disabledIds.indexOf(e.id) === -1)
							.map((e: any) => ({
								value: e,
								label: entity2String(section.targetEntity, e),
							})),
					);
					setLoading(false);
				})
				.catch(() => {
					if (!active) return;
					setOptions([]);
					setLoading(false);
				});
		}, 400);
		return () => {
			active = false;
			clearTimeout(timeout);
		};
	}, [text, search, disabledIds]);

	return (
		<Select
			value={null}
			inputValue={text}
			isDisabled={busy}
			isLoading={loading}
			options={options}
			isClearable={true}
			onInputChange={setText}
			onChange={(opt: any) => opt && onPick(opt.value)}
			placeholder={t`Start typing to search`}
			getOptionValue={R.path(['value', 'id'])}
			filterOption={R.always(true)}
		/>
	);
};

/** Editor (presentazionale) di una sezione relazione. */
const RelationEditor: React.FC<{
	section: RelationSection;
	sourceId: string;
	selectedItems: any[];
	pendingAddIds: string[];
	suggestions: any[];
	busy: boolean;
	onCreateNew?: () => void;
	onReorder?: (orderedIds: string[]) => void;
	onAdd: (entity: any) => void;
	onRemove: (entity: any) => void;
	onAddAll: () => void;
	onRemoveAll: () => void;
}> = ({
	section,
	sourceId,
	selectedItems,
	pendingAddIds,
	suggestions,
	busy,
	onCreateNew,
	onReorder,
	onAdd,
	onRemove,
	onAddAll,
	onRemoveAll,
}) => {
	const selectedIds = selectedItems.map((i) => i.id);
	const displayedSuggestions = suggestions.filter(
		(s) => selectedIds.indexOf(s.id) === -1,
	);
	const [dragId, setDragId] = useState<string | null>(null);
	const reorderable = !!(section.orderable && onReorder);
	const handleDrop = (targetId: string) => {
		if (!dragId || dragId === targetId || !onReorder) {
			setDragId(null);
			return;
		}
		const ids = selectedItems.map((i) => i.id);
		const from = ids.indexOf(dragId);
		const to = ids.indexOf(targetId);
		if (from < 0 || to < 0) {
			setDragId(null);
			return;
		}
		ids.splice(to, 0, ids.splice(from, 1)[0]);
		onReorder(ids);
		setDragId(null);
	};

	return (
		<div>
			{onCreateNew && (
				<div className="margin-bottom">
					<Button size="sm" color="primary" disabled={busy} onClick={onCreateNew}>
						<i className="fa fa-plus" />{' '}
						{/* le certificazioni non si creano: si associano all'azienda quelle già esistenti */}
						{section.createKind === 'companyCertification'
							? t`Relations360/createLink`
							: t`create new`}
					</Button>
				</div>
			)}

			{!section.suggestionsOnly && (
				<RelationSearchSelect
					section={section}
					disabledIds={[...selectedIds, sourceId]}
					busy={busy}
					onPick={onAdd}
				/>
			)}

			{section.suggestionField && (
				<div className="margin-top">
					<h5>
						{t`Relations360/suggested`}
						{displayedSuggestions.length > 0 && (
							<Button size="sm" color="link" disabled={busy} onClick={onAddAll}>
								{t`Relations360/selectAllSuggested`}
							</Button>
						)}
					</h5>
					{displayedSuggestions.length === 0 ? (
						<p className="text-muted">{t`Relations360/noSuggestions`}</p>
					) : (
						<SuggestionChips
							key={section.field}
							section={section}
							suggestions={displayedSuggestions}
							busy={busy}
							onAdd={onAdd}
						/>
					)}
				</div>
			)}

			<div className="margin-top">
				<h5>
					{t`Relations360/selected`}
					{selectedItems.length > 0 && (
						<Button
							size="sm"
							color="link"
							className="text-danger"
							disabled={busy}
							onClick={onRemoveAll}
						>
							{t`Relations360/removeAllSelected`}
						</Button>
					)}
				</h5>
				{selectedItems.length === 0 ? (
					<p className="text-muted">{t`Relations360/noSelected`}</p>
				) : reorderable ? (
					selectedItems.map((item) => (
						<div
							key={item.id}
							draggable={!busy}
							onDragStart={() => setDragId(item.id)}
							onDragOver={(e) => e.preventDefault()}
							onDrop={() => handleDrop(item.id)}
							style={{
								display: 'flex',
								alignItems: 'center',
								padding: '6px 8px',
								margin: '4px 0',
								border: '1px solid #d0d0d0',
								borderRadius: 6,
								background:
									dragId === item.id
										? '#eef3ff'
										: pendingAddIds.indexOf(item.id) > -1
										? '#eaf7ee'
										: '#f7f7f7',
								cursor: busy ? 'default' : 'grab',
							}}
						>
							<i
								className="fa fa-bars"
								style={{ marginRight: 10, color: '#999' }}
							/>
							<span style={{ flex: 1 }}>
								{entity2String(section.targetEntity, item)}
							</span>
							<button
								type="button"
								className="btn btn-sm btn-link text-danger"
								style={{ padding: 0 }}
								disabled={busy}
								onClick={() => onRemove(item)}
							>
								<i className="fa fa-minus" />
							</button>
						</div>
					))
				) : (
					selectedItems.map((item) => (
						<Chip
							key={item.id}
							label={entity2String(section.targetEntity, item)}
							icon="minus"
							color="#dc3545"
							busy={busy}
							pending={pendingAddIds.indexOf(item.id) > -1}
							onClick={() => onRemove(item)}
						/>
					))
				)}
			</div>
		</div>
	);
};

interface Relations360DialogProps {
	entity: any;
	entityName: ENTITY;
	isOpen: boolean;
	onClose: () => void;
	// sezioni edit iniettate dal modale specifico per tipo (vuoto per i tipi senza dati editabili)
	editSections?: EditSectionSpec[];
	// campi relazione da nascondere (es. Fiere/Certs quando non è una Company)
	hiddenRelationFields?: string[];
	// richiamato quando il chiamante deve ricaricare i propri dati edit (dopo un salvataggio)
	onReloadEdit?: () => void;
}

export const Relations360Dialog: React.FC<Relations360DialogProps> = ({
	entity,
	entityName,
	isOpen,
	onClose,
	editSections = [],
	hiddenRelationFields = [],
	onReloadEdit,
}) => {
	// gli slot keyed possono valere solo per certe tipologie del record sorgente
	// (es. "Grafico evoluzione score" esiste solo sui macrotrend).
	// IMPORTANT - la dipendenza e' il PRIMITIVO sourceType e non l'oggetto entity: una
	// nuova identita' di entity (es. refetch del genitore) rigenererebbe sections ->
	// refetch -> l'effetto [isOpen, refetch], resettando la vista del dialogo aperto.
	const sourceType = entity && entity.trendType;
	const sections = useMemo(
		() =>
			getRelationSections(entityName).filter(
				(s) =>
					!s.forSourceTypes ||
					!sourceType ||
					s.forSourceTypes.indexOf(sourceType) > -1,
			),
		[entityName, sourceType],
	);
	const visibleRelationSections = useMemo(
		() => sections.filter((s) => hiddenRelationFields.indexOf(s.field) === -1),
		[sections, hiddenRelationFields],
	);

	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [active, setActive] = useState<UnifiedActive | null>(null);
	const [relationCreating, setRelationCreating] = useState(false);
	// sezioni edit già aperte almeno una volta: restano montate per conservare le modifiche
	const [visitedEdit, setVisitedEdit] = useState<string[]>([]);

	// sotto-vista a singola entità: finché è a video il footer espone le sue azioni al posto
	// del Salva globale
	const subFormHandlesRef = useRef<{ [id: string]: SubFormHandle }>({});
	const [subForm, setSubForm] = useState<{
		id: string;
		kind: 'create' | 'edit';
		isDirty: boolean;
	} | null>(null);
	const [subFormBusy, setSubFormBusy] = useState(false);

	// Scroll per vista: cambiando vista l'altezza del contenuto cambia e il browser tronca lo
	// scroll ad un valore arbitrario. L'elemento che scrolla è il `.modal`, non il `.modal-body`.
	const bodyRef = useRef<HTMLDivElement | null>(null);
	const scrollByView = useRef<{ [key: string]: number }>({});

	const baseViewKey = !active
		? 'list'
		: active.kind === 'edit'
		? 'edit:' + active.key
		: relationCreating
		? createSectionKey(active.relation.field)
		: 'relation:' + active.relation.field;
	const viewKey = subForm ? baseViewKey + '/' + subForm.id : baseViewKey;
	const viewKeyRef = useRef(viewKey);

	const getScroller = useCallback(
		(): HTMLElement | null =>
			bodyRef.current
				? (bodyRef.current.closest('.modal') as HTMLElement | null)
				: null,
		[],
	);

	/** Da chiamare PRIMA di ogni cambio di vista: fotografa lo scroll di quella corrente. */
	const snapshotScroll = useCallback(() => {
		const scroller = getScroller();
		if (scroller) {
			scrollByView.current[viewKeyRef.current] = scroller.scrollTop;
		}
	}, [getScroller]);

	// `loading` fra le dipendenze: il refetch mostra lo spinner e azzera lo scroll
	useLayoutEffect(() => {
		viewKeyRef.current = viewKey;
		const scroller = getScroller();
		if (scroller && !loading) {
			scroller.scrollTop = scrollByView.current[viewKey] || 0;
		}
	}, [viewKey, loading, getScroller]);

	// --- registro dei salvataggi delle sotto-viste (vedi relations360SaveRegistry) ---
	const editHandlesRef = useRef<{
		[id: string]: { sectionKey: string; handle: EditSaveHandle };
	}>({});
	const [dirtyEditSections, setDirtyEditSections] = useState<string[]>([]);
	// durante il salvataggio globale i ricaricamenti delle singole sezioni vanno soppressi: la shell
	// ricarica una volta sola alla fine
	const savingAllRef = useRef(false);

	const registerEditSave = useCallback<RegisterEditSave>(
		(id, sectionKey, handle) => {
			if (handle) {
				editHandlesRef.current[id] = { sectionKey, handle };
			} else {
				delete editHandlesRef.current[id];
			}
			const next = R.uniq(
				Object.keys(editHandlesRef.current)
					.filter((k) => editHandlesRef.current[k].handle.isDirty)
					.map((k) => editHandlesRef.current[k].sectionKey),
			).sort();
			setDirtyEditSections((prev) =>
				prev.length === next.length && prev.every((v, i) => v === next[i])
					? prev
					: next,
			);
		},
		[],
	);

	const registerSubForm = useCallback<RegisterSubForm>((id, handle) => {
		if (handle) {
			subFormHandlesRef.current[id] = handle;
		} else {
			delete subFormHandlesRef.current[id];
		}
		// ne può essere attiva una sola per volta: le viste sono mutuamente esclusive
		const ids = Object.keys(subFormHandlesRef.current);
		const activeId = ids.length ? ids[ids.length - 1] : null;
		const next = activeId
			? {
					id: activeId,
					kind: subFormHandlesRef.current[activeId].kind,
					isDirty: subFormHandlesRef.current[activeId].isDirty,
			  }
			: null;
		setSubForm((prev) => {
			if (!prev && !next) return prev;
			if (
				prev &&
				next &&
				prev.id === next.id &&
				prev.kind === next.kind &&
				prev.isDirty === next.isDirty
			) {
				return prev;
			}
			return next;
		});
	}, []);

	const [current, setCurrent] = useState<{ [field: string]: any[] }>({});
	const [links, setLinks] = useState<RelationLinks>(emptyLinks());
	const [suggestionsByField, setSuggestionsByField] = useState<{
		[field: string]: any[];
	}>({});
	const [pendingAdd, setPendingAdd] = useState<{ [field: string]: EntityMap }>(
		{},
	);
	const [pendingRemove, setPendingRemove] = useState<{
		[field: string]: EntityMap;
	}>({});
	// ordine esplicito dei collegati per le sezioni orderable (dopo un drag)
	const [orderOverride, setOrderOverride] = useState<{
		[field: string]: string[];
	}>({});

	const refetch = useCallback(async () => {
		if (!entity || !entity.id) return;
		setLoading(true);
		try {
			const [relRes, linkRes] = await Promise.all([
				client.query<any, any>({
					query: buildRelationsQuery(entityName),
					variables: { id: entity.id },
					// no-cache: letture effimere; i frammenti di display (es. Fair) non includono
					// sempre `id`, e scriverli nello store darebbe conflitti di normalizzazione.
					fetchPolicy: 'no-cache',
				}),
				client.query<any, any>({
					query: RELATIONS_LINKS_QUERY,
					variables: { id: entity.id },
					fetchPolicy: 'no-cache',
				}),
			]);

			const ent = (relRes.data && relRes.data.entity) || {};
			const cur: { [field: string]: any[] } = {};
			sections.forEach((s) => {
				const value = ent[s.field];
				// gli slot keyed espongono UN oggetto singolo (o null): lo normalizzo a
				// lista, cosi' il resto del componente resta uno solo per tutti i casi
				cur[s.field] = s.relationKey
					? value
						? [value]
						: []
					: value || [];
			});

			const suggResults = await Promise.all(
				sections
					.filter((s) => s.suggestionField)
					.map((s) =>
						client
							.query<any, any>({
								query: buildSuggestionsQuery(entityName, s),
								variables: { id: entity.id },
								fetchPolicy: 'no-cache',
							})
							.then((r) => ({
								field: s.field,
								list: prepareSuggestions(
									s,
									(r.data &&
										r.data.entity &&
										r.data.entity[s.suggestionField as string]) ||
										[],
								),
							}))
							.catch(() => ({ field: s.field, list: [] as any[] })),
					),
			);
			const sugg: { [field: string]: any[] } = {};
			suggResults.forEach((r) => {
				sugg[r.field] = r.list;
			});

			setCurrent(cur);
			setLinks(parseLinks(linkRes.data));
			setSuggestionsByField(sugg);
			setPendingAdd({});
			setPendingRemove({});
			setOrderOverride({});
		} finally {
			setLoading(false);
		}
	}, [entity, entityName, sections]);

	useEffect(() => {
		if (isOpen) {
			setActive(null);
			setVisitedEdit([]);
			scrollByView.current = {};
			refetch();
		}
	}, [isOpen, refetch]);

	const openEditSection = useCallback(
		(key: string) => {
			snapshotScroll();
			setActive({ kind: 'edit', key });
			setVisitedEdit((prev) => (prev.indexOf(key) > -1 ? prev : [...prev, key]));
		},
		[snapshotScroll],
	);

	// uscendo/cambiando sezione, esco sempre dalla sotto-vista "crea collegamento"
	useEffect(() => {
		setRelationCreating(false);
	}, [active]);

	const selectedItemsOf = useCallback(
		(field: string): any[] => {
			const cur = current[field] || [];
			const removed = pendingRemove[field] || {};
			const kept = cur.filter((i) => !removed[i.id]);
			const added = Object.keys(pendingAdd[field] || {}).map(
				(id) => (pendingAdd[field] as EntityMap)[id],
			);
			const base = [...kept, ...added];
			const order = orderOverride[field];
			if (!order) return base;
			// riordino secondo l'override; eventuali elementi non presenti (nuovi) vanno in coda
			const byId = new Map(base.map((i) => [i.id, i]));
			const ordered = order.map((id) => byId.get(id)).filter(Boolean) as any[];
			const rest = base.filter((i) => order.indexOf(i.id) === -1);
			return [...ordered, ...rest];
		},
		[current, pendingAdd, pendingRemove, orderOverride],
	);

	const pendingCount = useMemo(() => {
		let n = 0;
		sections.forEach((s) => {
			const added = Object.keys(pendingAdd[s.field] || {}).length;
			const removed = Object.keys(pendingRemove[s.field] || {}).length;
			// su uno slot keyed sostituire il collegato popola entrambe le mappe ma
			// resta UNA sola modifica (una riga riscritta)
			n += s.relationKey ? Math.max(added, removed) : added + removed;
		});
		return n;
	}, [sections, pendingAdd, pendingRemove]);

	// "sporco" = staging delle relazioni OPPURE una qualsiasi sezione edit con dati non salvati
	const isDirty =
		pendingCount > 0 ||
		Object.keys(orderOverride).length > 0 ||
		dirtyEditSections.length > 0;
	// conteggio mostrato sul bottone: modifiche di relazione + sezioni edit non salvate
	const changeCount = pendingCount + dirtyEditSections.length;

	const reloadSuggestions = useCallback(
		async (section: RelationSection) => {
			if (!section.suggestionField || !entity || !entity.id) return;
			try {
				const r = await client.query<any, any>({
					query: buildSuggestionsQuery(entityName, section),
					variables: { id: entity.id },
					fetchPolicy: 'no-cache',
				});
				const list = prepareSuggestions(
					section,
					(r.data &&
						r.data.entity &&
						r.data.entity[section.suggestionField as string]) ||
						[],
				);
				setSuggestionsByField((prev) => ({ ...prev, [section.field]: list }));
			} catch (e) {
				// ignoro
			}
		},
		[entity, entityName],
	);

	const addEntity = (field: string, ent: any) => {
		// un record non puo' essere collegato a se stesso (es. trend antitesi, aziende
		// simili): il backend lo rifiuterebbe facendo fallire l'INTERO salvataggio
		if (ent.id === entity.id) {
			return;
		}
		const section = sections.filter((s) => s.field === field)[0];
		if (section && section.relationKey) {
			// slot singolo: la nuova selezione sostituisce quella corrente
			setPendingRemove((prev) => ({
				...prev,
				[field]: (current[field] || []).reduce(
					(map: EntityMap, item: any) => {
						if (item.id !== ent.id) {
							map[item.id] = item;
						}
						return map;
					},
					{},
				),
			}));
			setPendingAdd((prev) => ({
				...prev,
				[field]: (current[field] || []).some((i: any) => i.id === ent.id)
					? {}
					: { [ent.id]: ent },
			}));
			return;
		}
		const cur = current[field] || [];
		const isCurrent = cur.some((i) => i.id === ent.id);
		if (isCurrent) {
			setPendingRemove((prev) => {
				const fieldMap = { ...(prev[field] || {}) };
				delete fieldMap[ent.id];
				return { ...prev, [field]: fieldMap };
			});
		} else {
			setPendingAdd((prev) => ({
				...prev,
				[field]: { ...(prev[field] || {}), [ent.id]: ent },
			}));
		}
	};

	const removeEntity = (field: string, ent: any) => {
		const wasPendingAdd = !!(pendingAdd[field] || {})[ent.id];
		if (wasPendingAdd) {
			setPendingAdd((prev) => {
				const fieldMap = { ...(prev[field] || {}) };
				delete fieldMap[ent.id];
				return { ...prev, [field]: fieldMap };
			});
		} else {
			setPendingRemove((prev) => ({
				...prev,
				[field]: { ...(prev[field] || {}), [ent.id]: ent },
			}));
		}
	};

	const removeAll = (section: RelationSection) => {
		const cur = current[section.field] || [];
		setPendingAdd((prev) => ({ ...prev, [section.field]: {} }));
		setPendingRemove((prev) => {
			const fieldMap = { ...(prev[section.field] || {}) };
			cur.forEach((ent) => {
				fieldMap[ent.id] = ent;
			});
			return { ...prev, [section.field]: fieldMap };
		});
	};

	const addAll = (section: RelationSection) => {
		const selectedIds = new Set(selectedItemsOf(section.field).map((i) => i.id));
		const toAdd = (suggestionsByField[section.field] || []).filter(
			(s) => !selectedIds.has(s.id),
		);
		if (toAdd.length === 0) return;
		setPendingAdd((prev) => {
			const fieldMap = { ...(prev[section.field] || {}) };
			toAdd.forEach((s) => {
				fieldMap[s.id] = s;
			});
			return { ...prev, [section.field]: fieldMap };
		});
	};

	const save = async () => {
		snapshotScroll();
		setSaving(true);
		savingAllRef.current = true;
		try {
			const pendingEdits = Object.keys(editHandlesRef.current)
				.map((k) => editHandlesRef.current[k])
				.filter((e) => e.handle.isDirty);
			for (const e of pendingEdits) {
				const res = await e.handle.save();
				if (res === false) {
					// porto l'utente sulla sezione che ha rifiutato il salvataggio, così vede il form
					// insieme all'errore di validazione
					if (editSections.some((s) => s.key === e.sectionKey)) {
						openEditSection(e.sectionKey);
					}
					return;
				}
			}

			const creates: RefPair[] = [];
			const deleteIds: string[] = [];
			sections.forEach((s) => {
				const addIds = Object.keys(pendingAdd[s.field] || {});
				const removeIds = Object.keys(pendingRemove[s.field] || {});
				const reordered = !!orderOverride[s.field];
				const changed = addIds.length > 0 || removeIds.length > 0 || reordered;

				if (s.relationKey) {
					if (!changed) {
						return;
					}
					const selected = selectedItemsOf(s.field)[0];
					if (selected) {
						// il backend sostituisce l'eventuale riga gia' presente per lo slot
						creates.push(
							keyedCreatePair(entity.id, selected.id, s.relationKey),
						);
					} else {
						deleteIds.push(...findKeyedJoinIds(links, s.relationKey));
					}
					return;
				}

				if (s.orderable && changed) {
					// riordino: ricreo TUTTI i collegati della sezione con l'orderIndex nuovo
					// (cancello i link esistenti dei correnti + creo i selezionati in ordine).
					(current[s.field] || []).forEach((it: any) => {
						deleteIds.push(...findJoinIds(links, s.targetEntity, it.localId));
					});
					selectedItemsOf(s.field).forEach((it: any, i: number) => {
						creates.push(...bidirectionalCreatePairs(entity.id, it.id, i));
					});
					return;
				}

				addIds.forEach((id) => {
					creates.push(...bidirectionalCreatePairs(entity.id, id));
				});
				removeIds.forEach((id) => {
					const ent = (pendingRemove[s.field] as EntityMap)[id];
					deleteIds.push(...findJoinIds(links, s.targetEntity, ent.localId));
				});
			});
			if (deleteIds.length > 0 || creates.length > 0) {
				await applyRelationships360Bulk(deleteIds, creates);
			}
			await refetch();
			if (onReloadEdit) onReloadEdit();
		} catch (e) {
			// IMPORTANT - il bulk e' transazionale: se fallisce NESSUNA modifica e' stata
			// salvata. Senza questo blocco l'errore sarebbe una promise rejection non
			// gestita e l'utente vedrebbe solo lo spinner fermarsi.
			const errs = parseGraphqlError(e, { entityName });
			sentryHandler(errs);
			modalHandler(errs);
		} finally {
			savingAllRef.current = false;
			setSaving(false);
		}
	};

	const handleClose = () => {
		if (isDirty && !window.confirm(t`Relations360/discardConfirm`)) return;
		onClose();
	};

	// La creazione inline è disponibile se la sezione ha createKind; fair/cert richiedono la
	// Company collegata, goodClaim no (è un record globale).
	const canCreateInSection = (relation: RelationSection) =>
		!!relation.createKind &&
		(relation.createKind === 'goodClaim' || !!entity.Company);

	const activeEditSpec =
		active && active.kind === 'edit'
			? editSections.find((s) => s.key === active.key) || null
			: null;

	const sourceLabel = entity2String(entityName, entity);
	const title = !active
		? `${t`Relations360/title`} — ${sourceLabel}`
		: active.kind === 'relation'
		? `${t`Relations360/editingPrefix`} ${t(active.relation.labelKey)} — ${sourceLabel}`
		: `${activeEditSpec ? t(activeEditSpec.labelKey) : ''} — ${sourceLabel}`;

	const onEditSaved = useCallback(() => {
		if (savingAllRef.current) return; 
		refetch();
		if (onReloadEdit) onReloadEdit();
	}, [refetch, onReloadEdit]);

	const activeRef = useRef<UnifiedActive | null>(null);
	activeRef.current = active;
	const onRelationCreated = useCallback(async () => {
		const current = activeRef.current;
		snapshotScroll();
		setRelationCreating(false);
		if (current && current.kind === 'relation') {
			await reloadSuggestions(current.relation);
		}
	}, [reloadSuggestions, snapshotScroll]);

	const showEditSection = !!activeEditSpec;
	const showRelationCreate =
		!showEditSection &&
		!!active &&
		active.kind === 'relation' &&
		relationCreating &&
		canCreateInSection(active.relation);

	const goBack = () => {
		snapshotScroll();
		setActive(null);
	};

	const cancelRelationCreate = useCallback(() => {
		snapshotScroll();
		setRelationCreating(false);
	}, [snapshotScroll]);

	/** Salva SOLO l'entità della sotto-vista, che si chiude da sé se va a buon fine. */
	const submitSubForm = async () => {
		const handle = subForm && subFormHandlesRef.current[subForm.id];
		if (!handle) return;
		setSubFormBusy(true);
		try {
			await handle.submit();
		} finally {
			setSubFormBusy(false);
		}
	};

	const cancelSubForm = () => {
		const handle = subForm && subFormHandlesRef.current[subForm.id];
		if (!handle) return;
		if (handle.isDirty && !window.confirm(t`Relations360/discardConfirm`)) return;
		handle.cancel();
	};

	return (
		<SubFormScope register={registerSubForm}>
			<Modal isOpen={isOpen} toggle={handleClose} size="lg">
				<ModalHeader toggle={handleClose}>{title}</ModalHeader>
				<ModalBody>
					{/* ModalBody è un componente funzione e non accetta una ref: risalgo da qui */}
					<div ref={bodyRef} />
					{/* Le sezioni edit già visitate restano montate anche quando non sono a video: così
					    tornando all'elenco (o passando ad un'altra sezione) le modifiche non ancora
					    salvate non vanno perse, e il Salva globale può applicarle tutte insieme. */}
					{editSections.map((spec) =>
						visitedEdit.indexOf(spec.key) === -1 ? null : (
							<div
								key={'edit_view_' + spec.key}
								style={{
									display:
										activeEditSpec && activeEditSpec.key === spec.key
											? undefined
											: 'none',
								}}
							>
								<EditSectionHost
									spec={spec}
									onSaved={onEditSaved}
									register={registerEditSave}
								/>
							</div>
						),
					)}

					{showEditSection ? null : loading ? (
						<Spinner show={true} />
					) : showRelationCreate && active && active.kind === 'relation' ? (
						<RelationCreateHost
							createKind={active.relation.createKind as any}
							connectedCompany={entity.Company}
							connectedId={entity.Company ? entity.Company.id : ''}
							sectionKey={createSectionKey(active.relation.field)}
							onSaved={onRelationCreated}
							onCancel={cancelRelationCreate}
							register={registerEditSave}
						/>
					) : active && active.kind === 'relation' ? (
						<RelationEditor
							section={active.relation}
							sourceId={entity.id}
							selectedItems={selectedItemsOf(active.relation.field)}
							pendingAddIds={Object.keys(pendingAdd[active.relation.field] || {})}
							suggestions={suggestionsByField[active.relation.field] || []}
							busy={saving}
							onCreateNew={
								canCreateInSection(active.relation)
									? () => {
											snapshotScroll();
											setRelationCreating(true);
									  }
									: undefined
							}
							onReorder={
								active.relation.orderable
									? (ids) => {
											const field = (active as { relation: RelationSection })
												.relation.field;
											setOrderOverride((prev) => ({ ...prev, [field]: ids }));
									  }
									: undefined
							}
							onAdd={(e) => addEntity(active.relation.field, e)}
							onRemove={(e) => removeEntity(active.relation.field, e)}
							onAddAll={() => addAll(active.relation)}
							onRemoveAll={() => removeAll(active.relation)}
						/>
					) : (
						<>
							{editSections.map((spec) => (
								<SectionCard
									key={'edit_' + spec.key}
									title={
										<>
											{t(spec.labelKey)}
											{dirtyEditSections.indexOf(spec.key) > -1 && (
												<UnsavedBadge />
											)}
										</>
									}
									onClick={() => openEditSection(spec.key)}
								>
									<span className="text-muted">{spec.summaryText}</span>
								</SectionCard>
							))}
							{visibleRelationSections.map((relation) => {
								const items = selectedItemsOf(relation.field);
								const pendingForSection =
									Object.keys(pendingAdd[relation.field] || {}).length +
									Object.keys(pendingRemove[relation.field] || {}).length;
								return (
									<SectionCard
										key={relation.field}
										title={
											<>
												{t(relation.labelKey)}{' '}
												<span className="text-muted">({items.length})</span>
												{pendingForSection > 0 && <UnsavedBadge />}
											</>
										}
										onClick={() => {
											snapshotScroll();
											setActive({ kind: 'relation', relation });
										}}
									>
										{items.length === 0 ? (
											<span className="text-muted">{t`Relations360/noSelected`}</span>
										) : (
											items.map((item) => (
												<span key={item.id} style={chipStyle}>
													{entity2String(relation.targetEntity, item)}
												</span>
											))
										)}
									</SectionCard>
								);
							})}
						</>
					)}
				</ModalBody>
				{/* i tre posti del footer non cambiano mai: nelle sotto-viste cambiano solo
				    etichetta e comportamento dei primi due, così i bottoni restano dove sono */}
				<ModalFooter>
					{subForm ? (
						<Button
							color="secondary"
							disabled={subFormBusy}
							onClick={cancelSubForm}
						>
							<i className="fa fa-arrow-left" /> {t`cancel`}
						</Button>
					) : (
						active && (
							<Button color="secondary" disabled={saving} onClick={goBack}>
								<i className="fa fa-arrow-left" /> {t`Back`}
							</Button>
						)
					)}
					{subForm ? (
						<Button
							color="success"
							disabled={subFormBusy}
							onClick={submitSubForm}
						>
							{subFormBusy
								? t`Relations360/saving`
								: subForm.kind === 'create'
								? t`create`
								: t`save`}
						</Button>
					) : (
						// unico salva del dialogo: vale per le relazioni e per tutte le sezioni edit
						<Button
							color="success"
							disabled={!isDirty || saving || loading}
							onClick={save}
						>
							{saving
								? t`Relations360/saving`
								: `${t`save`}${isDirty ? ` (${changeCount})` : ''}`}
						</Button>
					)}
					<Button
						color="link"
						disabled={saving || subFormBusy}
						onClick={handleClose}
					>
						{t`close`}
					</Button>
				</ModalFooter>
			</Modal>
		</SubFormScope>
	);
};